import type { BattleCopy, BattleRound, BattleSeat, BattleSummary } from "@loot-os/api-contract";
import type { BattleCardTag } from "@loot-os/design-system/domain/battles/BattleCard";
import type {
  BattleRoundBox,
  BattleSeatView,
} from "@loot-os/design-system/domain/battles/battleTypes";

/**
 * How the three battle pages show a battle's parts: its seats (each player's opening their
 * profile), its tags and its rounds, in the store's words (`defaultBattleCopy`).
 */

/** Whether a seat is the viewer's own: a player's, holding the viewer's end-user id. */
function isViewerSeat(seat: BattleSeat, viewerEndUserId: string | null | undefined): boolean {
  return seat.kind === "player" && seat.endUser !== null && seat.endUser.id === viewerEndUserId;
}

/** How a seat reads to the viewer: You, a bot, another player, or empty. */
function seatLabel(seat: BattleSeat, isViewer: boolean, seats: BattleCopy["seats"]): string {
  if (seat.kind === "empty") return seats.empty;
  if (seat.kind === "bot") return seats.bot;
  if (isViewer) return seats.you;
  return seat.endUser?.displayName ?? seats.player;
}

/**
 * One seat as the viewer sees it: "You" for their own, the player's name, "Bot", "Empty seat".
 * A player's seat opens their profile (boxed opens its quick view from every seat); a bot's or
 * an empty seat has no one to show.
 */
export function toSeatView(
  seat: BattleSeat,
  viewerEndUserId: string | null | undefined,
  showProfile: (endUserId: string) => void,
  words: BattleCopy,
): BattleSeatView {
  const isViewer = isViewerSeat(seat, viewerEndUserId);
  const label = seatLabel(seat, isViewer, words.seats);
  const endUser = seat.kind === "player" ? seat.endUser : null;
  return {
    label,
    kind: seat.kind,
    isViewer,
    isWinner: seat.isWinner === true,
    profile: endUser
      ? {
          label: words.profileLabel(isViewer ? null : label),
          onShow: () => showProfile(endUser.id),
        }
      : undefined,
  };
}

/** The mode (with its rule), Private and Fast spin, as a battle's head and card tag it. */
export function battleTags(
  battle: Pick<BattleSummary, "mode" | "isPublic" | "fastSpin">,
  words: BattleCopy,
): BattleCardTag[] {
  return words.tags(battle).map(({ key, label, hint }) => {
    const tag: BattleCardTag = { label };
    if (hint !== undefined) tag.hint = hint;
    // The mode takes the color of how it is won: crazy, the lowest total, in the danger tone.
    if (key === "mode") tag.tone = battle.mode === "crazy" ? "danger" : "accent";
    return tag;
  });
}

/** The battle's boxes in round order; a box the page can't name falls back to the store's word. */
export function roundBoxes(
  rounds: readonly BattleRound[],
  boxes: ReadonlyMap<string, BattleRoundBox>,
  fallbackName: string,
): BattleRoundBox[] {
  return rounds.map((round) => boxes.get(round.boxId) ?? { name: fallbackName, imageUrl: "" });
}
